"use client";

import React from "react";
import { Breadcrumb } from "@bfl-ui-toolkit/breadcrumb-v2";
import { IYouAreHere } from "../../interfaces/IAMCContent.interface";
import { dataLayer, CombinedClevertapProps } from "@repo/analytics/data-layer-utils";
import { selectLoginStatus, useAppSelector } from "@repo/global-store";

interface AMCBreadcrumbProps {
  breadcrumbData: IYouAreHere;
  fundName: string;
  amcName?: string;
}

export const AMCBreadcrumb: React.FC<AMCBreadcrumbProps> = ({
  breadcrumbData,
  fundName = "",
  amcName = "",
}) => {
  const { title = "", links = [] } = breadcrumbData ?? {};

  const loginStatus = useAppSelector(selectLoginStatus);

  const formattedItems: Record<string, { label: string; link?: string }> = {};

  Object.entries(links?.[0] ?? {}).forEach(([key, item]) => {
    if (!item) return;

    formattedItems[key] = {
      label: item.label ?? "",
      ...(item.url && { link: item.url }),
    };
  });

  formattedItems[`item${Object.keys(formattedItems).length}`] = {
    label: fundName,
  };

  const breadcrumbContent = {
    "event-prop-heading": title,
    componentPostion: "",
    componentTitle: "Breadcrumb",
    heading: title,
    hideCurrent: "false",
    startLevel: "4",
    showOnLoggedIn: "false",
    disableShadowing: "false",
    breadcrumbItem: [formattedItems],
  };

  const handleBreadcrumbTrackEvent = (eventType: string) => {
    const payload = {
      component: "amc_breadcrumb",
      element: "breadcrumb_click",
      event: "click",
      event_type: eventType,
      destination_url: "NA",
      section_title: title,
      cta_text: fundName,
      product_name: "bajaj_mutual_fund",
      category_name: "mutual_fund",
      page_type: "pdp",
      component_position: "",
      component_id: "amc_breadcrumb",
      customer_tag: loginStatus,
      is_loggedin: loginStatus,
    };

    dataLayer(loginStatus, payload, payload as unknown as CombinedClevertapProps);
  };

  const trackEvent = (payload?: { clickType?: string }) => {
    if (payload?.clickType) {
      handleBreadcrumbTrackEvent(payload.clickType);
    }
  };

  const orderedItems = Object.values(formattedItems).sort((a, b) => {
    if (!a.link && b.link) return 1;
    if (a.link && !b.link) return -1;
    return 0;
  });

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: orderedItems.map(({ label, link }, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: (label ?? "").replace("${companyName}", amcName),
      item: link || undefined,
    })),
  };

  return (
    <>
      <div className="mx-[-16px] my-[-12px] md:m-0">
        <Breadcrumb trackEvent={trackEvent} content={breadcrumbContent} />
      </div>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
    </>
  );
};

export default AMCBreadcrumb;
